'use client'

import AppointmentCoupon from '@/components/appointment-coupon'
import { useContext, useEffect, useState } from 'react'
import { stripHtml } from '@/lib/utils/strip-html'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { GuideValidityLine } from '@/components/guide-validity-line'
import {
    computeRenewedGuideValidUntil,
    useGuideValidityDays,
} from '@/lib/guide-validity'
import { useAuth } from '@/store/hooks'
import {
    approveOrderBudget,
    renewOrderGuide,
    exportOrderBudgetPdf,
    getOrder,
    cancelOrder,
    transferOrderSeller,
    generateOrderBilling,
    enableOrderFlexPayment,
    getOrderSettlement,
    requestOrderSettlement,
    type OrderSettlement,
    deleteOrder,
    printOrder,
    markOrderBillingAsPaid,
    cancelOrderBilling,
    Order,
    getOrderStatusLabel,
    getOrderStatusColor,
    sendOrderBudgetEmail,
    sendOrderFlexLink,
    rescheduleOrderComponent,
    changeOrderStatus,
    PROCEDURE_STATUS_LABELS,
    updateOrderServiceAddress,
    type OrderStatus,
} from '@/lib/api/orders/order-api'
import { PdfDelivery } from '@/lib/download-blob'
import { profitMargin } from '@/lib/billing/financials'
import BillingInfoSection from '@/components/billing/BillingInfoSection'
import BillingChildrenSections from '@/components/billing/billing-children-sections'
import { BillingInfoData } from '@/components/billing/BillingInfoSection'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import PresenceTimeline from '@/components/presence/presence-timeline'
import {
    MdArrowBack,
    MdLocationOn,
    MdManageAccounts,
    MdPersonPin,
    MdEdit,
    MdCancel,
    MdSwapHoriz,
    MdPriceCheck,
    MdReceiptLong,
    MdDelete,
    MdOpenInNew,
    MdPerson,
    MdCalendarToday,
    MdMedicalServices,
    MdAttachFile,
    MdAccessTime,
    MdRoom,
    MdBuild,
    MdGroup,
    MdListAlt,
    MdBusiness,
    MdPictureAsPdf,
    MdDownload,
    MdPrint,
    MdEmail,
    MdEvent,
    MdEventRepeat,
    MdInventory2,
    MdAccountBalanceWallet,
    MdContentCopy,
    MdCheckCircle,
    MdTrendingUp,
    MdScience,
    MdLocalHospital,
    MdVaccines,
    MdFlag,
} from 'react-icons/md'
import { FaWhatsapp } from 'react-icons/fa'
import ApproveBudgetModal from '@/components/service-address/approve-budget-modal'
import ChangeServiceAddressModal from '@/components/service-address/change-service-address-modal'
import ServiceAddressCard from '@/components/service-address/service-address-card'
import SettleOrderModal from '@/components/settle-order-modal'
import GenerateBillingModal from '@/components/generate-billing-modal'
import { ServiceAddress } from '@/components/service-address/service-address-section'
import ConfirmModal from '@/components/confirm-modal'
import ActionsMenu, { ActionMenuItem } from '@/components/actions-menu'
import ProntuarioMenu from '@/components/prontuario/prontuario-menu'
import CancelAppointmentModal from '@/components/medical/cancel-appointment-modal'
import RescheduleComponentModal from '@/components/medical/reschedule-component-modal'
import TransferSellerModal from '@/components/medical/transfer-seller-modal'
import ScheduleAppointmentModal from '@/components/scheduling/schedule-appointment-modal'
import ChangeStatusModal from '@/components/bookings/ChangeStatusModal'
import OrderStoreSection from '@/components/bookings/order-store-section'
import { UserType } from '@/lib/api/common/interfaces/user.interface'
import CancellationNotice from '@/components/medical/cancellation-notice'
import { SignDocumentModal } from '@/components/signature/SignDocumentModal'
import PaymentRemaining from '@/components/billing/payment-remaining'
import { formatAppointmentDateTime } from '@mediardex/contracts'
import {
    resolveAppointmentBillingState,
    isClosedBillingStatus,
    type AppointmentAnchorLike,
} from '@/lib/billing/appointment-billing-state'

/**
 * Venda de PACOTE, não procedimento.
 *
 * O atendimento é o mesmo registro (`Order`), mas o que o operador vê tem outro
 * nome e outra leitura: os componentes vêm inclusos no preço fechado, o repasse é por
 * componente e há produtos entregues. Chamar isso de "procedimento" na tela faz o
 * operador procurar valores que, ali, são zero de propósito.
 */
const isPackageSale = (order: any): boolean => order?.packageCatalogId != null

/** Rótulos de quem recebe o repasse. Ver docs/catalog-package-pricing.md. */
const RECIPIENT_KIND_LABELS: Record<string, string> = {
    PARTNER: 'Parceiro',
    PROFESSIONAL: 'Profissional',
    HOSPITAL: 'Hospital',
    SUPPLIER: 'Fornecedor',
}

const formatCurrency = (cents: number): string =>
    `R$ ${(cents / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`

interface OrderDetailPageProps {
    /** Renderiza embutido (modal) para este id, ignorando a rota. */
    idOverride?: number
    /** Substitui o "voltar para a lista" por fechar o modal. */
    onBack?: () => void
}

export default function OrderDetailPage({
    idOverride,
    onBack,
}: OrderDetailPageProps = {}) {
    const navigate = useNavigate()
    const { user } = useAuth()
    const guideValidityDays = useGuideValidityDays(user?.companyId)
    const { canUpdate, canDelete } = useResourcePermissions(
        'bookings',
        'orders'
    )
    // Fora da saúde: Pedido, e quem recebe é cliente/aluno/tutor.
    const { ClientLabel } = useClientLabel()
    const {
        order: orderLabel,
        Order: OrderLabel,
        Orders: OrdersLabel,
        Consultations: ConsultationsLabel,
        Exams: ExamsLabel,
    } = useServiceLabel()
    const { id: paramId } = useParams<{ id: string }>()
    const id = idOverride ? String(idOverride) : paramId
    const { socket, isConnected } = useContext(SocketContext)

    // Pagamento confirmado pelo gateway: o webhook invalida o cache DESTE
    // atendimento e emite este evento. Sem escutar aqui, a cobrança seguia
    // aparecendo como pendente na tela até o operador recarregar.
    useEffect(() => {
        if (!socket || !isConnected || !id) return
        const eventName = `appointment-billing-order-${id}`
        const handle = () => loadOrder()
        socket.on(eventName, handle)
        return () => {
            socket.off(eventName, handle)
        }
    }, [socket, isConnected, id])
    /** No modal, sair da lista significa fechar; na rota, navegar. */
    const goBack = () => (onBack ? onBack() : navigate('/bookings/orders'))
    const [order, setOrder] = useState<Order | null>(null)

    /**
     * Venda fechada na vitrine. A tela é a mesma do atendimento, e algumas coisas dela não
     * existem em mercadoria: guia de convênio, e o vocabulário clínico do título.
     */
    const isStoreSale = order?.origin === 'STORE'
    /** Venda de balcão: mesma mercadoria, fechada pelo operador no painel. */
    const isCounterSale = (order as any)?.origin === 'COUNTER'
    /**
     * VENDA DE MERCADORIA, venha da vitrine ou do balcão.
     *
     * O que a distingue do atendimento é o mesmo nos dois casos: não há executante, guia de
     * convênio, instrução de preparo nem "procedimento" nenhum — há produto entregue. Onde
     * a regra é "isto não existe em mercadoria", o recorte é ESTE, não `isStoreSale`:
     * checar só a loja deixaria a venda de balcão exibindo vocabulário clínico.
     *
     * Fica aqui em cima, e não perto do JSX, porque as MENSAGENS de cancelar e excluir
     * também precisam dele — "Procedimento cancelado" depois de estornar uma venda de
     * shampoo é o mesmo vazamento de vocabulário, só que num toast.
     */
    const isGoodsSale = isStoreSale || isCounterSale
    /** Como este registro se chama nas mensagens ao operador. */
    const RecordLabel = isGoodsSale ? 'Venda' : OrderLabel
    const recordLabel = isGoodsSale ? 'a venda' : orderLabel
    /**
     * Rótulo para os modais que o encaixam em frase masculina ("saldo em aberto DO
     * {entityLabel}", "a primeira cobrança DESTE {entityLabel}").
     *
     * Por isso "pedido" e não "venda": o feminino produziria "do venda". Trocar a
     * concordância dentro de cada modal seria mexer em componente compartilhado por causa
     * de um caso — e o pedido é o nome certo da venda de mercadoria de qualquer forma.
     */
    const entityLabel = isGoodsSale ? 'pedido' : 'procedimento'

    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    /**
     * Componente cuja remarcação está aberta.
     *
     * Num pacote, remarcar é por LINHA: a cirurgia adia e a consulta fica. Ver
     * docs/catalog-package-pricing.md.
     */
    const [reschedulingComponent, setReschedulingComponent] = useState<{
        id: number
        name: string
        scheduledAt?: string | null
        arrivalOrder?: boolean
    } | null>(null)
    const [reschedulingComponentLoading, setReschedulingComponentLoading] =
        useState(false)
    const [cancelling, setCancelling] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [transferring, setTransferring] = useState(false)
    const [showTransferModal, setShowTransferModal] = useState(false)
    const [showScheduleModal, setShowScheduleModal] = useState(false)
    const [showStatusModal, setShowStatusModal] = useState(false)
    const [savingStatus, setSavingStatus] = useState(false)
    const [printing, setPrinting] = useState<boolean>(false)
    const [showSignModal, setShowSignModal] = useState<boolean>(false)
    const [showApproveModal, setShowApproveModal] = useState(false)
    const [showAddressModal, setShowAddressModal] = useState(false)
    const [savingAddress, setSavingAddress] = useState(false)
    /** Aprovação em pagamento flex — mesmo modal, sem gerar cobrança. */
    const [showApproveFlexModal, setShowApproveFlexModal] = useState(false)
    const [approvingBudget, setApprovingBudget] = useState(false)
    const [printingBudget, setPrintingBudget] = useState(false)
    const [sendingBudgetEmail, setSendingBudgetEmail] = useState(false)
    /** Reenvio manual do link flex ao paciente (whatsapp/email). */
    const [sendingFlexLink, setSendingFlexLink] = useState<
        'whatsapp' | 'email' | null
    >(null)

    useEffect(() => {
        if (id) {
            loadOrder()
        }
    }, [id])

    const loadOrder = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getOrder(parseInt(id, 10))
            if (response.success && response.result) {
                setOrder(response.result)
            } else {
                toast.error(response.error || `Erro ao carregar ${orderLabel}`)
                goBack()
            }
        } catch (error) {
            toast.error(`Erro ao carregar ${orderLabel}`)
            goBack()
        } finally {
            setLoading(false)
        }
    }

    /**
     * `delivery` decide entre abrir a impressão e salvar o arquivo. "Imprimir"
     * abre o diálogo direto — o operador quer o papel, não um PDF na pasta de
     * downloads; "Baixar PDF" fica como ação separada no menu.
     */
    const handlePrint = async (delivery: PdfDelivery = 'print') => {
        if (!order) return
        setPrinting(true)
        try {
            if (order.status === 'BUDGET') {
                await exportOrderBudgetPdf(order.id, delivery)
            } else {
                await printOrder(order.id, delivery)
            }
            if (delivery === 'download')
                toast.success('PDF gerado com sucesso!')
        } catch (error) {
            toast.error('Erro ao gerar PDF')
        } finally {
            setPrinting(false)
        }
    }

    const handleDelete = () => {
        setShowDeleteModal(true)
    }

    /**
     * Cancelar não é excluir: o procedimento permanece na listagem como "Cancelado".
     * Se a cobrança estava paga, o backend responde `refundPending` — o dinheiro só
     * volta depois que um operador financeiro aprovar o estorno.
     */
    const confirmCancel = async (reason: string) => {
        if (!id || !order) return

        setCancelling(true)
        try {
            const response = await cancelOrder(parseInt(id, 10), reason)
            if (response.success) {
                if (response.result?.refundPending) {
                    toast.success(
                        `${RecordLabel} ${isGoodsSale ? 'cancelada' : 'cancelado'}. A solicitação de estorno foi enviada para aprovação de um operador financeiro.`
                    )
                } else {
                    toast.success(
                        `${RecordLabel} ${isGoodsSale ? 'cancelada' : 'cancelado'} com sucesso!`
                    )
                }
                setShowCancelModal(false)
                await loadOrder()
            } else {
                toast.error(response.error || `Erro ao cancelar ${recordLabel}`)
            }
        } catch (error) {
            toast.error(`Erro ao cancelar ${orderLabel}`)
        } finally {
            setCancelling(false)
        }
    }

    // Transferir a venda (createdBy) para outro usuário. Autorização real no backend
    // (só COMPANY ou o vendedor atual); a ação nem aparece para os demais.
    const confirmTransferSeller = async (sellerId: number) => {
        if (!id || !order) return
        setTransferring(true)
        try {
            const response = await transferOrderSeller(
                parseInt(id, 10),
                sellerId
            )
            if (response.success) {
                toast.success('Vendedor transferido com sucesso!')
                setShowTransferModal(false)
                await loadOrder()
            } else {
                toast.error(response.error || 'Erro ao transferir vendedor')
            }
        } catch {
            toast.error('Erro ao transferir vendedor')
        } finally {
            setTransferring(false)
        }
    }

    // Gerar cobrança para um procedimento que ficou sem cobrança. Após criar, vai à cobrança.
    const [generatingBilling, setGeneratingBilling] = useState(false)
    const [showGenerateBillingModal, setShowGenerateBillingModal] =
        useState(false)
    /**
     * `amount` ausente = cobra o saldo inteiro. O valor vem daqui, e não da tela de
     * edição da cobrança, porque alterar o valor de uma cobrança atrelada é proibido
     * — é por ali que sairia desconto sem autorização.
     */
    const handleGenerateBilling = async (amount?: number) => {
        if (!id || !order) return
        setGeneratingBilling(true)
        try {
            const response = await generateOrderBilling(
                parseInt(id, 10),
                amount
            )
            if (response.success && response.result?.billingId) {
                toast.success('Cobrança gerada com sucesso!')
                setShowGenerateBillingModal(false)
                // Vai para a EDIÇÃO (escolher forma de pagamento, vencimento etc.), com
                // `from=schedule` para exibir cliente/parceiro herdados como no agendamento.
                navigate(
                    `/financial/billings/${response.result.billingId}/edit?from=schedule`
                )
            } else {
                toast.error(response.error || 'Erro ao gerar cobrança')
            }
        } catch {
            toast.error('Erro ao gerar cobrança')
        } finally {
            setGeneratingBilling(false)
        }
    }

    // Ativar pagamento flex num procedimento já aprovado. Cancela as cobranças
    // em aberto e reabre para o cliente pagar avulso — daí o modal de confirmação.
    const [showEnableFlexModal, setShowEnableFlexModal] = useState(false)
    const [showSettleModal, setShowSettleModal] = useState(false)
    const [settlement, setSettlement] = useState<OrderSettlement | null>(null)
    const [loadingSettlement, setLoadingSettlement] = useState(false)
    const [requestingSettlement, setRequestingSettlement] = useState(false)
    const [enablingFlex, setEnablingFlex] = useState(false)
    const handleEnableFlex = async () => {
        if (!id || !order) return
        setEnablingFlex(true)
        try {
            const response = await enableOrderFlexPayment(parseInt(id, 10))
            if (response.success) {
                toast.success(
                    'Pagamento flex ativado. O cliente passa a pagar pelo portal.'
                )
                setShowEnableFlexModal(false)
                loadOrder()
            } else {
                toast.error(response.error || 'Erro ao ativar o pagamento flex')
            }
        } catch {
            toast.error('Erro ao ativar o pagamento flex')
        } finally {
            setEnablingFlex(false)
        }
    }

    /**
     * Abre a confirmação da quitação buscando os valores no backend.
     *
     * A tela sabe estimar (tem o `billingState`), mas quem confirma precisa ver o
     * número exato que será lançado de desconto e o repasse que sai — e é o
     * backend que sabe se já existe solicitação na fila.
     */
    const handleOpenSettleModal = async () => {
        if (!id) return
        setSettlement(null)
        setShowSettleModal(true)
        setLoadingSettlement(true)
        try {
            const response = await getOrderSettlement(parseInt(id, 10))
            if (response.success && response.result) {
                setSettlement(response.result)
            } else {
                toast.error(
                    response.error || 'Erro ao carregar os valores da quitação'
                )
                setShowSettleModal(false)
            }
        } catch {
            toast.error('Erro ao carregar os valores da quitação')
            setShowSettleModal(false)
        } finally {
            setLoadingSettlement(false)
        }
    }

    const handleRequestSettlement = async () => {
        if (!id) return
        setRequestingSettlement(true)
        try {
            const response = await requestOrderSettlement(parseInt(id, 10))
            if (response.success) {
                toast.success(
                    'Quitação solicitada. Ela precisa ser aprovada nas aprovações financeiras.'
                )
                setShowSettleModal(false)
                loadOrder()
            } else {
                toast.error(response.error || 'Erro ao solicitar a quitação')
            }
        } catch {
            toast.error('Erro ao solicitar a quitação')
        } finally {
            setRequestingSettlement(false)
        }
    }

    const flexLink = (order as any)?.publicToken
        ? `${window.location.origin}/p/${(order as any).publicToken}`
        : ''

    const handleCopyFlexLink = async () => {
        if (!flexLink) return
        try {
            await navigator.clipboard.writeText(flexLink)
            toast.success('Link copiado')
        } catch {
            toast.error('Não foi possível copiar')
        }
    }

    const handleSendFlexLink = async (channel: 'whatsapp' | 'email') => {
        if (!id) return
        setSendingFlexLink(channel)
        try {
            const res = await sendOrderFlexLink(parseInt(id, 10), channel)
            if (res.success) {
                toast.success(res.result?.message || 'Link enviado.')
            } else {
                toast.error(res.error || 'Não foi possível enviar o link.')
            }
        } catch {
            toast.error('Não foi possível enviar o link.')
        } finally {
            setSendingFlexLink(null)
        }
    }

    const [showRenewGuideModal, setShowRenewGuideModal] = useState(false)
    const [renewingGuide, setRenewingGuide] = useState(false)
    const handleRenewGuide = async () => {
        if (!id) return
        setRenewingGuide(true)
        try {
            const response = await renewOrderGuide(parseInt(id, 10))
            if (response.success) {
                toast.success('Guia renovada com sucesso')
                setShowRenewGuideModal(false)
                await loadOrder()
            } else {
                toast.error(response.error || 'Erro ao renovar a guia')
            }
        } catch {
            toast.error('Erro ao renovar a guia')
        } finally {
            setRenewingGuide(false)
        }
    }

    const confirmDelete = async () => {
        if (!id || !order) return

        setDeleting(true)
        try {
            const response = await deleteOrder(parseInt(id, 10))
            if (response.success) {
                toast.success(
                    `${RecordLabel} ${isGoodsSale ? 'excluída' : 'excluído'} com sucesso!`
                )
                goBack()
            } else {
                toast.error(response.error || `Erro ao excluir ${recordLabel}`)
            }
        } catch (error) {
            toast.error(`Erro ao excluir ${orderLabel}`)
        } finally {
            setDeleting(false)
            setShowDeleteModal(false)
        }
    }

    const isBudget = order?.status === 'BUDGET'
    /**
     * Atendimento coberto por convênio: quem paga é a operadora, por fatura.
     * A cobrança existe (faturada ao convênio, `amount` zero e o valor em
     * `invoicedAmount`), mas não há link, gateway nem forma de pagamento a
     * escolher — e o modo flex, que é o paciente gerando o próprio pagamento no
     * portal, não se aplica. Ver docs/health-insurance-system.md §2.
     */
    const coveredByHealthPlan = !!(order as any)?.healthPlanId
    const isExpired =
        isBudget && order?.validUntil && new Date(order.validUntil) < new Date()

    // Pagamento em partes: soma das cobranças VIVAS (âncora + parciais). O botão "Gerar
    // cobrança" continua disponível enquanto essa soma não fechar o valor de venda
    // (finalPrice); cada nova cobrança pendura na âncora via parentBillingId. Cobrança
    // encerrada (cancelada/estornada/baixada como perda) não conta — ver o helper.
    const anchorBilling = (order as any)?.billing as
        | AppointmentAnchorLike
        | null
        | undefined
    const orderFinalPrice = (order as any)?.finalPrice ?? 0
    const billingState = resolveAppointmentBillingState(
        anchorBilling,
        orderFinalPrice
    )
    // Quanto ainda falta ENTRAR para quitar o total do procedimento (pagamento em partes).
    // Só é relevante em cenário PARCIAL: a soma cobrada ainda não fechou o total, ou já
    // existem cobranças parciais atreladas. Para uma cobrança única cheia só aguardando
    // pagamento, o aviso seria redundante com o próprio valor — então não exibimos.
    const isPartialPaymentScenario =
        !!anchorBilling &&
        orderFinalPrice > 0 &&
        (billingState.billedSoFar < orderFinalPrice ||
            (anchorBilling.adjustments ?? []).some(
                a => !isClosedBillingStatus(a.status)
            ))
    const remainingToComplete = isPartialPaymentScenario
        ? billingState.remainingToPay
        : null
    // `shouldBecomeAnchor` cobre o caso "nada vivo, nada pago" mesmo com valor 0: a ação
    // continua visível para que o backend responda "defina um valor no agendamento", em
    // vez de o item sumir do menu sem explicação.
    const canGenerateBilling =
        billingState.shouldBecomeAnchor || billingState.canGenerate

    /**
     * "Marcar como quitado": o paciente pagou parte, nada mais está a receber, e a
     * soma nunca vai fechar o combinado — então o repasse jamais nasceria (ele só
     * sai na cobrança que CRUZA o total). Deriva do estado já carregado para não
     * gastar uma chamada por visita; o backend revalida tudo ao solicitar.
     */
    const canSettleWithDiscount =
        !isBudget &&
        order?.status !== 'CANCELLED' &&
        orderFinalPrice > 0 &&
        billingState.paidSoFar > 0 &&
        billingState.paidSoFar < orderFinalPrice &&
        // Nada A RECEBER em aberto. ⚠️ Não é `hasLiveBilling`: naquele helper,
        // cobrança PAGA também é "viva" (encerradas são só cancelada, estornada e
        // baixada como perda), e com isso a ação nunca apareceria — todo caso de
        // quitação tem, por definição, pelo menos uma cobrança paga.
        ![
            ...(anchorBilling ? [anchorBilling] : []),
            ...(anchorBilling?.adjustments ?? []),
        ].some(c => !isClosedBillingStatus(c.status) && c.status !== 'PAID')

    /** O endereço confirmado no modal vira o local de atendimento do procedimento. */
    /**
     * `flex` aprova SEM gerar cobrança: quem paga é o cliente, em parcelas
     * avulsas pelo portal. Por isso o fluxo flex nunca redireciona para o
     * financeiro — não há cobrança para abrir; só recarrega a tela, que passa a
     * exibir o selo do modo.
     */
    /**
     * Troca o local de atendimento já gravado. Endpoint próprio: a edição completa
     * mexeria em preço e cobrança, que nada têm a ver com corrigir o endereço.
     */
    const handleChangeServiceAddress = async (address: ServiceAddress) => {
        if (!order) return
        setSavingAddress(true)
        try {
            const response = await updateOrderServiceAddress(order.id, address)
            if (response.success) {
                toast.success('Local de atendimento atualizado')
                setShowAddressModal(false)
                await loadOrder()
            } else {
                toast.error(
                    response.error || 'Erro ao alterar o local de atendimento'
                )
            }
        } catch {
            toast.error('Erro ao alterar o local de atendimento')
        } finally {
            setSavingAddress(false)
        }
    }

    const handleApproveBudget = async (
        address: ServiceAddress,
        payment?: { method: string; installments: number | null },
        flex = false
    ) => {
        if (!order) return
        setApprovingBudget(true)
        try {
            const response = await approveOrderBudget(order.id, {
                ...address,
                ...(flex ? { flex: true } : {}),
                // Forma fechada na aprovação: a cobrança nasce com método e
                // parcelamento, e o link já pode ir sem passar pela tela de cobrança.
                ...(payment && !flex
                    ? {
                          paymentMethod: payment.method,
                          installmentCount: payment.installments ?? undefined,
                      }
                    : {}),
            } as any)
            if (response.success) {
                toast.success(
                    flex
                        ? 'Orçamento aprovado em pagamento flex. O cliente pagará pelo portal.'
                        : 'Orçamento aprovado com sucesso!'
                )
                const billingId = (response.result as any)?.billingId
                // Só desvia para a cobrança quando ela nasceu SEM método — que é a
                // única coisa que faltava definir lá. Com a forma escolhida aqui, o
                // operador fica no atendimento.
                // Cobrança de convênio não tem forma de pagamento a definir
                // (sem gateway, sem link): mandar o operador para o financeiro
                // seria mandá-lo a uma tela onde não há o que fazer.
                if (billingId && !flex && !payment && !coveredByHealthPlan) {
                    navigate(
                        `/financial/billings/${billingId}/edit?from=budget`
                    )
                } else {
                    loadOrder()
                }
            } else {
                toast.error(response.error || 'Erro ao aprovar orçamento')
            }
        } catch {
            toast.error('Erro ao aprovar orçamento')
        } finally {
            setApprovingBudget(false)
            setShowApproveModal(false)
            setShowApproveFlexModal(false)
        }
    }

    const handlePrintBudget = async () => {
        if (!order) return
        setPrintingBudget(true)
        try {
            await exportOrderBudgetPdf(order.id)
            toast.success('PDF do orçamento gerado com sucesso!')
        } catch {
            toast.error('Erro ao gerar PDF do orçamento')
        } finally {
            setPrintingBudget(false)
        }
    }

    const handleSendBudgetEmail = async () => {
        if (!order) return
        setSendingBudgetEmail(true)
        try {
            const response = await sendOrderBudgetEmail(order.id)
            if (response.success) {
                toast.success('Orçamento enviado por email com sucesso!')
            } else {
                toast.error(response.error || 'Erro ao enviar orçamento')
            }
        } catch {
            toast.error('Erro ao enviar orçamento por email')
        } finally {
            setSendingBudgetEmail(false)
        }
    }

    /**
     * Troca manual de status. Cancelado fica fora das opções: cancelar mexe em
     * cobrança, estorno e agenda, e tem ação própria (o backend recusa por aqui).
     */
    /**
     * Voltar para ORÇAMENTO só enquanto não houver cobrança viva: orçamento é o
     * estado de antes de existir dinheiro, e cobrança em aberto continua pagável
     * pelo link público. O backend recusa de qualquer forma — aqui a opção some
     * para o operador não esbarrar no erro.
     */
    const hasLiveBilling = !!(
        order?.billing &&
        [order.billing, ...((order.billing as any).adjustments ?? [])].some(
            (b: any) => b?.status !== 'CANCELLED'
        )
    )

    const statusOptions = (
        Object.entries(PROCEDURE_STATUS_LABELS) as [string, string][]
    )
        .filter(([value]) => value !== 'CANCELLED')
        .filter(([value]) => value !== 'BUDGET' || !hasLiveBilling)
        .map(([value, label]) => ({ value, label }))

    const handleChangeStatus = async (status: string) => {
        setSavingStatus(true)
        try {
            const res = await changeOrderStatus(
                order!.id,
                status as OrderStatus
            )
            if (res.success) {
                toast.success('Status alterado.')
                setShowStatusModal(false)
                await loadOrder()
            } else {
                toast.error(res.error || 'Não foi possível alterar o status.')
            }
        } finally {
            setSavingStatus(false)
        }
    }

    /**
     * Ações secundárias do cabeçalho. A primária (aprovar, num orçamento) fica fora
     * do menu.
     *
     * Montada antes dos early-returns, então `order` pode ser null — o menu só é
     * renderizado depois deles.
     */
    const orderActions: ActionMenuItem[] = order
        ? [
              ...(!isBudget
                  ? [
                        {
                            key: 'export-pdf',
                            label: 'Exportar PDF',
                            icon: <MdPictureAsPdf />,
                            onClick: () => setShowSignModal(true),
                            title: 'Exportar PDF com assinatura digital',
                        },
                    ]
                  : []),
              {
                  key: 'print',
                  label: isBudget ? 'Imprimir orçamento' : 'Imprimir',
                  icon: <MdPrint />,
                  onClick: () => handlePrint('print'),
                  loading: printing,
              },
              {
                  key: 'download',
                  label: isBudget ? 'Baixar orçamento' : 'Baixar PDF',
                  icon: <MdDownload />,
                  onClick: () => handlePrint('download'),
                  loading: printing,
              },
              ...(isBudget && canUpdate
                  ? [
                        {
                            key: 'budget-email',
                            label: 'Enviar por e-mail',
                            icon: <MdEmail />,
                            onClick: handleSendBudgetEmail,
                            loading: sendingBudgetEmail,
                        },
                    ]
                  : []),
              // Procedimento cancelado não é editável (o backend recusa): editar o valor geraria
              // cobrança complementar ou estorno para um atendimento que não vai acontecer.
              // Marcar/remarcar a data sem atravessar a tela de edição, que carrega
              // o atendimento inteiro para quem só precisa dizer "é quinta às 14h".
              // Orçamento fica de fora: data é coisa de atendimento aprovado.
              // Mercadoria não se agenda: ela é entregue. Oferecer "Agendar" numa venda
              // marcaria uma data que nada consome — e o pedido nasce SEM `scheduledAt`
              // justamente para ficar fora do lembrete de comparecimento.
              ...(canUpdate &&
              order.status !== 'CANCELLED' &&
              !isBudget &&
              !isGoodsSale
                  ? [
                        {
                            key: 'schedule',
                            label: order.scheduledAt ? 'Remarcar' : 'Agendar',
                            icon: <MdEvent />,
                            onClick: () => setShowScheduleModal(true),
                        },
                    ]
                  : []),
              // O local é confirmado uma vez (agendamento ou aprovação do orçamento) e
              // depois disso não havia como mexer: a tela de edição não mostra o campo.
              // Fora em orçamento (o local ainda não existe) e em venda de mercadoria,
              // onde o endereço é o de ENTREGA e tem seção própria.
              ...(canUpdate &&
              order.status !== 'CANCELLED' &&
              !isBudget &&
              !isGoodsSale
                  ? [
                        {
                            key: 'change-service-address',
                            label: 'Alterar endereço de atendimento',
                            icon: <MdLocationOn />,
                            onClick: () => setShowAddressModal(true),
                        },
                    ]
                  : []),
              // Régua do operador: a importação trouxe atendimento marcado como
              // concluído sem ter acontecido, e não havia como corrigir.
              ...(canUpdate && order.status !== 'CANCELLED'
                  ? [
                        {
                            key: 'change-status',
                            label: 'Alterar status',
                            icon: <MdFlag />,
                            onClick: () => setShowStatusModal(true),
                        },
                    ]
                  : []),
              // "Editar" leva ao formulário de AGENDAMENTO, que não sabe ler uma venda de
              // mercadoria: abriria a tela pedindo executante e data para um pedido que não
              // tem nem um nem outro. Venda se desfaz cancelando ou estornando a cobrança,
              // que é o que devolve o estoque.
              ...(canUpdate && order.status !== 'CANCELLED' && !isGoodsSale
                  ? [
                        {
                            key: 'edit',
                            label: 'Editar',
                            icon: <MdEdit />,
                            onClick: () =>
                                navigate(
                                    `/atendimento/scheduling/${order.id}?type=PROCEDURE`
                                ),
                        },
                    ]
                  : []),
              // Transferir vendedor: só o vendedor atual (createdBy) ou um administrador
              // (userType COMPANY). O backend reforça; aqui a ação nem aparece para os demais.
              ...((user?.userType === UserType.COMPANY ||
                  user?.id === order.createdBy?.id) &&
              order.status !== 'CANCELLED'
                  ? [
                        {
                            key: 'transfer-seller',
                            label: 'Transferir vendedor',
                            icon: <MdSwapHoriz />,
                            onClick: () => setShowTransferModal(true),
                        },
                    ]
                  : []),
              // Gerar cobrança: enquanto as cobranças atreladas não fecharem o valor total
              // (pagamento em partes). Sem cobrança ainda, ou a soma parcial < finalPrice.
              // Não em orçamento nem cancelado (o backend recusa).
              ...(canUpdate &&
              canGenerateBilling &&
              !isBudget &&
              order.status !== 'CANCELLED'
                  ? [
                        {
                            key: 'generate-billing',
                            label: 'Gerar cobrança',
                            icon: <MdReceiptLong />,
                            onClick: () => setShowGenerateBillingModal(true),
                            loading: generatingBilling,
                        },
                    ]
                  : []),
              // Marcar como quitado: pago a MENOS que o combinado, sem nada a receber.
              // Sem isto o repasse fica preso para sempre — ele só nasce na cobrança que
              // faz a soma paga cruzar o total, e um desconto no fechamento nunca cruza.
              // Não executa: abre solicitação nas aprovações financeiras.
              ...(canUpdate && canSettleWithDiscount
                  ? [
                        {
                            key: 'settle-with-discount',
                            label: 'Marcar como quitado',
                            icon: <MdPriceCheck />,
                            onClick: handleOpenSettleModal,
                            loading: loadingSettlement,
                        },
                    ]
                  : []),
              // Ativar pagamento flex: só em procedimento aprovado que ainda NÃO
              // está em flex. Converte para o modo em que o cliente paga avulso
              // pelo portal (cancela as cobranças em aberto — ver o modal).
              // Flex é o paciente pagando o procedimento em parcelas avulsas pelo portal.
              // Mercadoria já saiu da prateleira: não há o que parcelar depois da entrega.
              ...(canUpdate &&
              !isBudget &&
              order.status !== 'CANCELLED' &&
              !coveredByHealthPlan &&
              !isGoodsSale &&
              !(order as any).flexPayment
                  ? [
                        {
                            key: 'enable-flex',
                            label: 'Ativar pagamento flex',
                            icon: <MdAccountBalanceWallet />,
                            onClick: () => setShowEnableFlexModal(true),
                            loading: enablingFlex,
                        },
                    ]
                  : []),
              // Renovar guia: só em atendimento (orçamento tem validade própria). Redefine
              // o vencimento da guia para hoje + dias configurados em "Validade das Guias".
              // Guia é documento de convênio: venda de mercadoria não emite nenhuma.
              ...(canUpdate &&
              !isBudget &&
              order.status !== 'CANCELLED' &&
              !isGoodsSale
                  ? [
                        {
                            key: 'renew-guide',
                            label: 'Renovar guia',
                            icon: <MdEventRepeat />,
                            onClick: () => setShowRenewGuideModal(true),
                            loading: renewingGuide,
                        },
                    ]
                  : []),
              // Cancelar preserva o registro; excluir apaga. São ações distintas.
              // Concluído TAMBÉM pode ser cancelado — ver nota na consulta.
              ...(canUpdate && order.status !== 'CANCELLED'
                  ? [
                        {
                            key: 'cancel',
                            label: order.packageCatalogId
                                ? 'Cancelar pacote'
                                : `Cancelar ${orderLabel}`,
                            icon: <MdCancel />,
                            onClick: () => setShowCancelModal(true),
                            danger: true,
                            loading: cancelling,
                        },
                    ]
                  : []),
              ...(canDelete
                  ? [
                        {
                            key: 'delete',
                            label: 'Excluir',
                            icon: <MdDelete />,
                            onClick: handleDelete,
                            danger: true,
                            loading: deleting,
                        },
                    ]
                  : []),
          ]
        : []

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando detalhes do procedimento...
                </p>
            </div>
        )
    }

    if (!order) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdPersonPin className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        {OrderLabel} não encontrado
                    </h3>
                    <p className="text-sm text-base-content/60">
                        O {orderLabel} que você está procurando não existe
                    </p>
                </div>
                <button onClick={() => goBack()} className="btn btn-secondary">
                    Voltar para {OrdersLabel}
                </button>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => goBack()}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex lg:flex rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                            <MdPersonPin className="text-2xl text-white" />
                        </div>
                        <div>
                            {/* Venda de loja não tem item âncora (`catalog`): quem
                                nomeia é o `title`, montado dos itens do carrinho. */}
                            <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                {order.catalog?.name ||
                                    (isPackageSale(order)
                                        ? 'Pacote'
                                        : isGoodsSale
                                          ? order.title ||
                                            (isCounterSale
                                                ? 'Venda de produtos'
                                                : 'Compra na loja')
                                          : OrderLabel)}
                            </h1>
                            <p className="text-sm text-base-content/60">
                                {/* Mercadoria é VENDA, em qualquer segmento: chamar a venda
                                    de um shampoo de "Procedimento do Paciente" é o nome
                                    clínico vazando para onde não vale. */}
                                {isCounterSale
                                    ? `Venda de balcão ao ${ClientLabel.toLowerCase()}`
                                    : isStoreSale
                                      ? `Pedido da loja${order.storeConfig?.name ? ` ${order.storeConfig.name}` : ''}`
                                      : isPackageSale(order)
                                        ? `Pacote vendido ao ${ClientLabel.toLowerCase()}`
                                        : `${OrderLabel} do ${ClientLabel}`}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Aprovar é o que a pessoa veio fazer num orçamento: fica em
                        evidência, fora do menu. */}
                    {isBudget && canUpdate && (
                        <button
                            onClick={() => setShowApproveModal(true)}
                            disabled={approvingBudget}
                            className="btn btn-success btn-sm gap-2"
                            title="Aprovar Orçamento"
                        >
                            {approvingBudget ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <MdCheckCircle className="text-lg" />
                            )}
                            <span className="hidden sm:inline">Aprovar</span>
                        </button>
                    )}
                    {/* Flex: aprova sem cobrança — o cliente paga em parcelas
                        avulsas pelo portal. Fica ao lado do aprovar normal
                        porque é uma escolha de COMO cobrar, feita no mesmo
                        momento, não uma ação secundária.

                        Some no atendimento coberto por convênio: ali quem paga é
                        a operadora, e o backend recusa. */}
                    {isBudget && canUpdate && !coveredByHealthPlan && (
                        <button
                            onClick={() => setShowApproveFlexModal(true)}
                            disabled={approvingBudget}
                            className="btn btn-outline btn-success btn-sm gap-2"
                            title="Aprovar sem gerar cobrança — o cliente paga em parcelas avulsas pelo portal"
                        >
                            <MdAccountBalanceWallet className="text-lg" />
                            <span className="hidden sm:inline">
                                Aprovar (flex)
                            </span>
                        </button>
                    )}
                    {/* Prontuário do paciente sem sair do atendimento (só no segmento saúde). */}
                    <ProntuarioMenu
                        clientId={order.client?.id}
                        clientName={order.client?.name}
                    />
                    <ActionsMenu items={orderActions} />
                </div>
            </div>

            {order.status === 'CANCELLED' && (
                <CancellationNotice
                    entityLabel={order.packageCatalogId ? 'pacote' : orderLabel}
                    cancelledAt={order.cancelledAt}
                    cancellationReason={order.cancellationReason}
                />
            )}

            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <span
                                className={`badge ${getOrderStatusColor(
                                    order.status,
                                    order.scheduledAt
                                )} text-white`}
                            >
                                {getOrderStatusLabel(
                                    order.status,
                                    order.scheduledAt
                                )}
                            </span>
                            {isBudget && isExpired && (
                                <span className="badge badge-error text-white">
                                    Expirado
                                </span>
                            )}
                            {/* O selo de flex saiu daqui: o card "Pagamento Flex —
                                Link do Paciente" abaixo já sinaliza o modo flex,
                                evitando informação duplicada. */}
                            {isBudget && order.validUntil && !isExpired && (
                                <span className="text-xs text-base-content/60">
                                    Válido até{' '}
                                    {format(
                                        new Date(order.validUntil),
                                        'dd/MM/yyyy'
                                    )}
                                </span>
                            )}
                            {order.approvedAt && order.approver && (
                                <span className="text-xs text-success">
                                    Aprovado em{' '}
                                    {format(
                                        new Date(order.approvedAt),
                                        'dd/MM/yyyy'
                                    )}{' '}
                                    por {order.approver.name}
                                </span>
                            )}
                            {order.packageCatalogId != null && (
                                <span className="badge badge-success">
                                    Pacote
                                </span>
                            )}
                            {order.catalog?.catalogType && (
                                <span className="badge badge-ghost">
                                    {order.catalog.catalogType.name}
                                </span>
                            )}
                        </div>
                        {order.scheduledAt && (
                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                <MdCalendarToday />
                                <span>
                                    Agendado para{' '}
                                    {format(
                                        new Date(order.scheduledAt),
                                        "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                        { locale: ptBR }
                                    )}
                                </span>
                            </div>
                        )}
                        {/* Guia é documento de convênio para levar ao prestador. Venda de
                            mercadoria não tem guia — a linha só confundiria o operador. */}
                        {!isGoodsSale && (
                            <GuideValidityLine
                                issuedAt={order.createdAt}
                                days={guideValidityDays}
                                guideValidUntil={order.guideValidUntil}
                            />
                        )}
                    </div>
                </div>

                <div className="p-6">
                    {/* Pagamento flex: link exclusivo do procedimento. O link já é
                        enviado automaticamente ao aprovar/ativar o flex; aqui o
                        operador reenvia por WhatsApp/e-mail ou copia para
                        compartilhar. Só existe quando o procedimento é flex. */}
                    {(order as any).flexPayment &&
                        (order as any).publicToken && (
                            <div className="mb-6 p-4 rounded-xl border border-secondary/30 bg-secondary/5">
                                <div className="flex items-center gap-2 mb-3">
                                    <MdAccountBalanceWallet className="text-secondary" />
                                    <p className="font-semibold text-sm">
                                        Pagamento Flex - Link do Paciente
                                    </p>
                                </div>
                                <a
                                    href={flexLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block text-sm text-secondary break-all hover:underline mb-3"
                                >
                                    {flexLink}
                                </a>
                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleCopyFlexLink}
                                        className="btn btn-secondary btn-sm gap-1"
                                    >
                                        <MdContentCopy className="text-base" />
                                        Copiar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleSendFlexLink('whatsapp')
                                        }
                                        disabled={sendingFlexLink !== null}
                                        className="btn btn-outline btn-secondary btn-sm gap-1"
                                    >
                                        {sendingFlexLink === 'whatsapp' ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <FaWhatsapp className="text-base" />
                                        )}
                                        WhatsApp
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleSendFlexLink('email')
                                        }
                                        disabled={sendingFlexLink !== null}
                                        className="btn btn-outline btn-secondary btn-sm gap-1"
                                    >
                                        {sendingFlexLink === 'email' ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdEmail className="text-base" />
                                        )}
                                        E-mail
                                    </button>
                                </div>
                            </div>
                        )}
                    {/* O QUE foi feito vem antes de para quem e de quanto: é a
                        primeira coisa que se procura ao abrir o atendimento. */}
                    {order.catalog && (
                        <div className="mb-6">
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-info/10 rounded-lg">
                                    <MdListAlt className="text-xl text-info" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        {isPackageSale(order)
                                            ? 'Pacote do Catálogo'
                                            : isGoodsSale
                                              ? 'Produto do Catálogo'
                                              : `${OrderLabel} do Catálogo`}
                                    </p>
                                    <p className="font-semibold">
                                        {order.catalog.name}
                                    </p>
                                    {order.catalog.description && (
                                        <p className="text-sm text-base-content/60 mt-1 whitespace-pre-wrap">
                                            {stripHtml(
                                                order.catalog.description
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {order.client && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdPerson className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Paciente
                                    </p>
                                    {/* O nome leva ao cadastro do paciente: quem abre o
                                        atendimento quase sempre precisa do histórico, do
                                        contato ou do convênio dele, e antes tinha de sair e
                                        buscar na listagem de clientes. */}
                                    <button
                                        onClick={() =>
                                            navigate(
                                                `/person/clients/${order.client!.id}`
                                            )
                                        }
                                        className="font-semibold link link-hover text-left inline-flex items-center gap-1"
                                        title="Ver cadastro do paciente"
                                    >
                                        {order.client.name}
                                        <MdOpenInNew className="text-sm text-base-content/40" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {order.professional && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdMedicalServices className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Profissional
                                    </p>
                                    <p className="font-semibold">
                                        {order.professional.name}
                                    </p>
                                    {order.professional.registrationNumber && (
                                        <p className="text-sm text-base-content/60">
                                            {
                                                order.professional
                                                    .registrationNumber
                                            }
                                        </p>
                                    )}
                                    {/* O valor mora JUNTO de quem recebe. Num card
                                        separado, "Valor do Profissional" obrigava a
                                        cruzar duas partes da tela para saber quanto
                                        aquele nome recebe. */}
                                    {(order as any).professionalAmount > 0 && (
                                        <p className="text-sm font-semibold text-secondary mt-1">
                                            Repasse:{' '}
                                            {formatCurrency(
                                                (order as any)
                                                    .professionalAmount
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Local de atendimento: fica ao lado do paciente e do
                            profissional porque é o que a recepção precisa dizer ao
                            telefone. Venda de mercadoria usa o mesmo campo para o
                            endereço de ENTREGA, que tem seção própria. */}
                        {!isBudget && !isGoodsSale && (
                            <ServiceAddressCard
                                address={order}
                                onChange={
                                    canUpdate && order.status !== 'CANCELLED'
                                        ? () => setShowAddressModal(true)
                                        : undefined
                                }
                            />
                        )}

                        {order.createdBy && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-info/10 rounded-lg">
                                    <MdManageAccounts className="text-xl text-info" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Vendido por
                                    </p>
                                    <p className="font-semibold">
                                        {order.createdBy.name}
                                    </p>
                                    {order.createdBy.email && (
                                        <p className="text-sm text-base-content/60">
                                            {order.createdBy.email}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {order.partner && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-accent/10 rounded-lg">
                                    <MdBusiness className="text-xl text-accent" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Parceiro
                                    </p>
                                    <p className="font-semibold">
                                        {order.partner.name}
                                    </p>
                                    {order.partner.tradingName && (
                                        <p className="text-sm text-base-content/60">
                                            {order.partner.tradingName}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {order.hospital && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-error/10 rounded-lg">
                                    <MdLocalHospital className="text-xl text-error" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Hospital
                                    </p>
                                    <p className="font-semibold">
                                        {order.hospital.tradingName ||
                                            order.hospital.name}
                                    </p>
                                    {order.hospital.tradingName &&
                                        order.hospital.name !==
                                            order.hospital.tradingName && (
                                            <p className="text-sm text-base-content/60">
                                                {order.hospital.name}
                                            </p>
                                        )}
                                    {order.hospital.cnpj && (
                                        <p className="text-xs text-base-content/50 mt-1">
                                            CNPJ: {order.hospital.cnpj}
                                        </p>
                                    )}
                                    {(order as any).hospitalFee > 0 && (
                                        <p className="text-sm font-semibold text-secondary mt-1">
                                            Diária/taxa:{' '}
                                            {formatCurrency(
                                                (order as any).hospitalFee
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ANESTESISTA.
                            Fica com profissional e hospital porque é a mesma pergunta —
                            quem participou e quanto recebe. Ocupa a largura toda: além do
                            valor, precisa dizer QUEM recebe, e "hospital" sozinho não
                            responde qual. */}
                        {(order as any).anesthetistFee > 0 && (
                            <div className="md:col-span-2 flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-warning/10 rounded-lg">
                                    <MdMedicalServices className="text-xl text-warning" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Anestesista
                                    </p>
                                    <p className="font-semibold">
                                        {(order as any)
                                            .anesthetistRecipientName ||
                                            (RECIPIENT_KIND_LABELS[
                                                (order as any)
                                                    .anesthetistRecipientKind
                                            ] ??
                                                'Sem destinatário informado')}
                                    </p>
                                    {(order as any)
                                        .anesthetistRecipientKind && (
                                        <p className="text-sm text-base-content/60">
                                            Recebe como{' '}
                                            {(
                                                RECIPIENT_KIND_LABELS[
                                                    (order as any)
                                                        .anesthetistRecipientKind
                                                ] ?? ''
                                            ).toLowerCase()}
                                        </p>
                                    )}
                                    <p className="text-sm font-semibold text-secondary mt-1">
                                        Honorário:{' '}
                                        {formatCurrency(
                                            (order as any).anesthetistFee
                                        )}
                                    </p>
                                </div>
                            </div>
                        )}

                        {order.healthPlan && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-info/10 rounded-lg">
                                    <MdBusiness className="text-xl text-info" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Convênio
                                    </p>
                                    <p className="font-semibold">
                                        {order.healthPlan.name}
                                    </p>
                                    {order.healthPlan.ansCode && (
                                        <p className="text-xs text-base-content/50">
                                            ANS: {order.healthPlan.ansCode}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        {order.duration && (
                            <div className="flex items-center gap-2 p-3 bg-base-200/50 rounded-xl">
                                <MdAccessTime className="text-lg text-secondary" />
                                <div>
                                    <p className="text-xs text-base-content/60">
                                        Duração
                                    </p>
                                    <p className="font-medium">
                                        {order.duration} min
                                    </p>
                                </div>
                            </div>
                        )}

                        {((order as any).roomRef ||
                            (order as any).events?.[0]?.room ||
                            order.room) &&
                            (() => {
                                const roomData =
                                    (order as any).roomRef ||
                                    (order as any).events?.[0]?.room
                                return (
                                    <div className="flex items-center gap-2 p-3 bg-base-200/50 rounded-xl">
                                        <MdRoom className="text-lg text-secondary" />
                                        <div>
                                            <p className="text-xs text-base-content/60">
                                                Sala / Consultório
                                            </p>
                                            <p className="font-medium">
                                                {roomData?.name || order.room}
                                            </p>
                                            {roomData?.description && (
                                                <p className="text-xs text-base-content/60">
                                                    {roomData.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )
                            })()}

                        <div className="col-span-2 md:col-span-4">
                            <PresenceTimeline
                                kind="ORDER"
                                appointmentId={order.id}
                            />
                        </div>

                        {/* Cupom do agendamento ou da loja: `discountAmount` é o valor que ele abateu. */}
                        <AppointmentCoupon
                            code={(order as any).couponCode}
                            amount={(order as any).discountAmount}
                        />

                        {(order as any).costPrice != null ? (
                            <div className="col-span-2 md:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="flex items-center gap-2 p-3 bg-base-200/50 rounded-xl min-w-0">
                                    <div className="min-w-0">
                                        <p className="text-xs text-base-content/60">
                                            Custo
                                        </p>
                                        <p className="font-medium break-words">
                                            {(
                                                ((order as any).costPrice ||
                                                    0) / 100
                                            ).toLocaleString('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL',
                                            })}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 p-3 bg-secondary/10 rounded-xl min-w-0">
                                    <div className="min-w-0">
                                        <p className="text-xs text-base-content/60">
                                            Valor de venda
                                        </p>
                                        <p className="font-semibold text-secondary break-words">
                                            {(
                                                ((order as any).finalPrice ||
                                                    0) / 100
                                            ).toLocaleString('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL',
                                            })}
                                        </p>
                                    </div>
                                </div>
                                {(order as any).markupPercentage != null && (
                                    <div className="flex items-center gap-2 p-3 bg-base-200/50 rounded-xl min-w-0">
                                        <div className="min-w-0">
                                            <p className="text-xs text-base-content/60">
                                                Markup
                                            </p>
                                            <p className="font-medium break-words">
                                                {Number(
                                                    (order as any)
                                                        .markupPercentage
                                                ).toLocaleString('pt-BR', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                                %
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 p-3 bg-success/10 rounded-xl min-w-0">
                                    <div className="min-w-0">
                                        <p className="text-xs text-base-content/60">
                                            Lucro
                                        </p>
                                        <p className="font-semibold text-success break-words">
                                            {(
                                                (((order as any).finalPrice ||
                                                    0) -
                                                    ((order as any).costPrice ||
                                                        0)) /
                                                100
                                            ).toLocaleString('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL',
                                            })}
                                        </p>
                                        {/* Percentual do lucro é a MARGEM (sobre a
                                            venda). O card "Markup" ao lado é sobre o
                                            CUSTO — os dois números são diferentes para
                                            o mesmo pedido. */}
                                        {profitMargin(
                                            (order as any).finalPrice || 0,
                                            ((order as any).finalPrice || 0) -
                                                ((order as any).costPrice || 0)
                                        ) != null && (
                                            <p className="text-xs text-base-content/60">
                                                {profitMargin(
                                                    (order as any).finalPrice ||
                                                        0,
                                                    ((order as any)
                                                        .finalPrice || 0) -
                                                        ((order as any)
                                                            .costPrice || 0)
                                                )}
                                                % de margem
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (order as any).totalPrice != null &&
                          (order as any).totalPrice > 0 ? (
                            <div className="flex items-center gap-2 p-3 bg-secondary/10 rounded-xl min-w-0">
                                <div className="min-w-0">
                                    <p className="text-xs text-base-content/60">
                                        Valor Total
                                    </p>
                                    <p className="font-semibold text-secondary break-words">
                                        R${' '}
                                        {(
                                            (order as any).totalPrice / 100
                                        ).toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {order.catalog?.price != null &&
                                    order.catalog.price > 0 && (
                                        <div className="flex items-center gap-2 p-3 bg-base-200/50 rounded-xl min-w-0">
                                            <div className="min-w-0">
                                                <p className="text-xs text-base-content/60">
                                                    Preço de Custo
                                                </p>
                                                <p className="font-medium break-words">
                                                    R${' '}
                                                    {(
                                                        order.catalog.price /
                                                        100
                                                    ).toLocaleString('pt-BR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                {order.catalog?.sellingPrice != null &&
                                    order.catalog.sellingPrice > 0 && (
                                        <div className="flex items-center gap-2 p-3 bg-secondary/10 rounded-xl min-w-0">
                                            <div className="min-w-0">
                                                <p className="text-xs text-base-content/60">
                                                    Preço de Venda
                                                </p>
                                                <p className="font-semibold text-secondary break-words">
                                                    R${' '}
                                                    {(
                                                        order.catalog
                                                            .sellingPrice / 100
                                                    ).toLocaleString('pt-BR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                            </>
                        )}
                    </div>

                    {remainingToComplete && remainingToComplete > 0 && (
                        <div className="mb-6">
                            <PaymentRemaining
                                remainingToComplete={remainingToComplete}
                            />
                        </div>
                    )}

                    {order.description && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3">
                                Descrição
                            </h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-base-content whitespace-pre-wrap">
                                    {order.description}
                                </p>
                            </div>
                        </div>
                    )}

                    {order.equipment && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3 flex items-center gap-2">
                                <MdBuild className="text-xl" />
                                Equipamentos
                            </h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-base-content whitespace-pre-wrap">
                                    {order.equipment}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Loja e entrega — só no pedido nascido da vitrine (`origin = 'STORE'`). */}
            <OrderStoreSection
                order={order}
                canEdit={canUpdate}
                onSaved={loadOrder}
            />

            {/* Billing */}
            {order.billing && (
                <BillingInfoSection
                    // Cobrança pode ser agrupada: o card usa isto para mostrar quanto
                    // do total é deste atendimento.
                    appointmentSource={{
                        sourceType: 'PROCEDURE',
                        sourceId: order.id,
                    }}
                    billing={order.billing as BillingInfoData}
                    onMarkAsPaid={async () => {
                        const response = await markOrderBillingAsPaid(order.id)
                        if (response.success) {
                            const result = response.result as any
                            if (result?.pending) {
                                toast.info(
                                    result.message ||
                                        'A solicitação de aprovação para marcar como pago foi enviada'
                                )
                            } else {
                                toast.success('Cobrança marcada como paga!')
                            }
                            loadOrder()
                        } else {
                            toast.error(
                                response.error || 'Erro ao marcar como paga'
                            )
                        }
                    }}
                    onCancel={async () => {
                        const response = await cancelOrderBilling(order.id)
                        if (response.success) {
                            const result = response.result as any
                            if (result?.pending) {
                                toast.info(
                                    result.message ||
                                        'A solicitação de aprovação para cancelar a cobrança foi enviada'
                                )
                            } else {
                                toast.success('Cobrança cancelada!')
                            }
                            loadOrder()
                        } else {
                            toast.error(
                                response.error || 'Erro ao cancelar cobrança'
                            )
                        }
                    }}
                    canUpdate={canUpdate}
                    onBillingChanged={loadOrder}
                />
            )}

            {/* Complementos/parcelas: cada cobrança filha aparece com o MESMO bloco completo
                da principal (comprovante, marcar como pago etc.). O aviso "Falta X" fica só
                na âncora (acima), sem repetir aqui. */}
            <BillingChildrenSections
                adjustments={(order.billing as any)?.adjustments}
                onReload={loadOrder}
                canUpdate={canUpdate}
            />

            {(order.preInstructions || order.postInstructions) && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <h3 className="text-lg font-bold text-base-content">
                            Instruções
                        </h3>
                    </div>

                    <div className="p-6 space-y-6">
                        {order.preInstructions && (
                            <div>
                                <h4 className="font-semibold text-base-content mb-2">
                                    {isGoodsSale
                                        ? 'Antes da entrega'
                                        : `Pré-${OrderLabel}`}
                                </h4>
                                <div className="p-4 bg-warning/10 rounded-xl border border-warning/20">
                                    <p className="text-base-content whitespace-pre-wrap">
                                        {order.preInstructions}
                                    </p>
                                </div>
                            </div>
                        )}

                        {order.postInstructions && (
                            <div>
                                <h4 className="font-semibold text-base-content mb-2">
                                    {isGoodsSale
                                        ? 'Depois da entrega'
                                        : `Pós-${OrderLabel}`}
                                </h4>
                                <div className="p-4 bg-info/10 rounded-xl border border-info/20">
                                    <p className="text-base-content whitespace-pre-wrap">
                                        {order.postInstructions}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Consultas, exames e componentes do procedimento (OrderLinkedCatalog).
                Consultas e exames convivem na mesma tabela (distinção via catalog.category),
                então agrupamos por categoria para não mostrar consulta sob "Exames". */}

            {/* Itens do procedimento (multi-item: o operador empilha vários do
                catálogo, cada um com sua quantidade). A tela não mostrava esta
                lista: quem agendava dois do mesmo item via o total mais alto e não
                achava onde estava a quantidade. Cada unidade é um atendimento a
                mais — inclusive no repasse ao parceiro. */}
            {(() => {
                const items = (order as any).items ?? []
                if (items.length === 0) return false
                // Um item só, quantidade 1 e igual ao do topo: a lista repete o card
                // "Procedimento do Catálogo" e não acrescenta nada. Some.
                //
                // ⚠️ Não é "bloco de exames": estes são os itens do PRÓPRIO procedimento
                // (o operador empilha vários do catálogo, cada um com sua quantidade), e
                // cada unidade é um atendimento a mais — inclusive no repasse. Exames e
                // consultas atrelados são outra lista, logo abaixo.
                if (items.length > 1) return true
                const unico = items[0]
                return (
                    (unico.quantity ?? 1) > 1 ||
                    unico.variationName != null ||
                    unico.catalogId !== order.catalogId
                )
            })() && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-secondary/10 to-info/10 px-6 py-4 border-b border-base-300/50">
                        <div className="flex items-center gap-2">
                            {/* Mercadoria não é serviço clínico: o ícone do bisturi em cima
                                de uma lista de shampoo é o mesmo erro do vocabulário. */}
                            {isGoodsSale ? (
                                <MdInventory2 className="text-xl text-secondary" />
                            ) : (
                                <MdMedicalServices className="text-xl text-secondary" />
                            )}
                            <h3 className="text-lg font-bold text-base-content">
                                {isGoodsSale
                                    ? 'Produtos vendidos'
                                    : `Itens do ${OrderLabel}`}
                            </h3>
                            <span className="badge badge-secondary badge-sm">
                                {(order as any).items.length}
                            </span>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr className="text-xs text-base-content/50">
                                        <th>Item</th>
                                        <th className="text-right">
                                            Valor Unit.
                                        </th>
                                        <th className="text-center">Qtd.</th>
                                        <th className="text-right">Subtotal</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(order as any).items.map((item: any) => {
                                        const unit = item.unitPrice ?? 0
                                        const qtd = item.quantity ?? 1
                                        return (
                                            <tr key={item.id}>
                                                <td className="font-medium">
                                                    {item.catalog?.name ||
                                                        `Item #${item.catalogId}`}
                                                    {item.variationName && (
                                                        <p className="mt-0.5 text-xs font-normal text-base-content/60">
                                                            {item.variationName}
                                                        </p>
                                                    )}
                                                </td>
                                                <td className="text-right">
                                                    R${' '}
                                                    {(
                                                        unit / 100
                                                    ).toLocaleString('pt-BR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </td>
                                                <td className="text-center">
                                                    {qtd}
                                                </td>
                                                <td className="text-right font-medium text-secondary">
                                                    R${' '}
                                                    {(
                                                        (unit * qtd) /
                                                        100
                                                    ).toLocaleString('pt-BR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {order.exams &&
                order.exams.length > 0 &&
                (() => {
                    const isPackage = order.packageCatalogId != null
                    // Repasses da venda, agrupados pelo nome do componente que os gerou.
                    const payoutByComponent = new Map<string, any[]>()
                    for (const r of (order as any).componentPayouts ?? []) {
                        if (!r.sourceLabel) continue
                        const lista = payoutByComponent.get(r.sourceLabel) ?? []
                        lista.push(r)
                        payoutByComponent.set(r.sourceLabel, lista)
                    }
                    const CATEGORY_LABELS: Record<string, string> = {
                        CONSULTATION: `${ConsultationsLabel} do ${OrderLabel}`,
                        EXAM: `${ExamsLabel} do ${OrderLabel}`,
                        PROCEDURE: OrdersLabel,
                        PACKAGE: 'Pacotes',
                        MEDICATION: 'Medicamentos',
                        PRODUCT: 'Produtos',
                        SERVICE: 'Serviços',
                    }
                    const groups: Record<string, typeof order.exams> = {}
                    for (const ex of order.exams) {
                        const key = isPackage
                            ? 'PACKAGE'
                            : (ex.catalog as any)?.category || 'OUTROS'
                        ;(groups[key] ||= []).push(ex)
                    }
                    const titleFor = (key: string) =>
                        isPackage
                            ? 'Componentes do Pacote'
                            : CATEGORY_LABELS[key] || `Itens do ${OrderLabel}`

                    return Object.entries(groups).map(([key, items]) => (
                        <div
                            key={key}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden"
                        >
                            <div className="bg-gradient-to-r from-info/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                                <div className="flex items-center gap-2">
                                    <MdScience className="text-xl text-info" />
                                    <h3 className="text-lg font-bold text-base-content">
                                        {titleFor(key)}
                                    </h3>
                                    <span className="badge badge-info badge-sm">
                                        {items.length}
                                    </span>
                                </div>
                            </div>

                            <div className="p-6">
                                <div className="overflow-x-auto">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr className="text-xs text-base-content/50">
                                                <th>Item</th>
                                                <th>Tipo</th>
                                                <th className="text-right">
                                                    Preço Unit.
                                                </th>
                                                <th className="text-center">
                                                    Qtd.
                                                </th>
                                                <th className="text-right">
                                                    Subtotal
                                                </th>
                                                <th className="text-center">
                                                    Cortesia
                                                </th>
                                                {/* Quem recebe: é o que vira conta a
                                                    pagar. Sem esta coluna o dado fica
                                                    gravado e invisível, e o operador não
                                                    tem como conferir para quem o dinheiro
                                                    vai antes de a cobrança ser paga. */}
                                                <th>Quem recebe</th>
                                                {/* Venda de pacote: cada componente tem a
                                                    SUA data. Sem esta coluna, o operador
                                                    não enxerga quando cada parte acontece —
                                                    dado gravado e invisível. */}
                                                <th>Data</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map(exam => (
                                                <tr
                                                    key={exam.id}
                                                    className={
                                                        exam.complimentary
                                                            ? 'opacity-60'
                                                            : ''
                                                    }
                                                >
                                                    <td className="font-medium">
                                                        {exam.catalog?.name ||
                                                            '-'}
                                                        {exam.variationName && (
                                                            <p className="text-xs font-normal text-base-content/60 pl-3 border-l-2 border-base-300 mt-0.5">
                                                                {
                                                                    exam.variationName
                                                                }
                                                            </p>
                                                        )}
                                                    </td>
                                                    <td className="text-xs">
                                                        {exam.catalog
                                                            ?.catalogType
                                                            ?.name || '-'}
                                                    </td>
                                                    <td
                                                        className={`text-right ${exam.complimentary ? 'line-through text-base-content/40' : ''}`}
                                                    >
                                                        {/* Componente de pacote vale
                                                            ZERO de propósito: o paciente
                                                            paga o preço fechado. "R$
                                                            0,00" se lê como cadastro
                                                            errado. */}
                                                        {isPackage &&
                                                        !exam.unitPrice ? (
                                                            <span className="text-base-content/50">
                                                                Incluso
                                                            </span>
                                                        ) : (
                                                            `R$ ${(
                                                                exam.unitPrice /
                                                                100
                                                            ).toLocaleString(
                                                                'pt-BR',
                                                                {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2,
                                                                }
                                                            )}`
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {exam.quantity}
                                                    </td>
                                                    <td
                                                        className={`text-right font-medium ${exam.complimentary ? 'line-through text-base-content/40' : 'text-secondary'}`}
                                                    >
                                                        {isPackage &&
                                                        !exam.unitPrice
                                                            ? '—'
                                                            : `R$ ${(
                                                                  (exam.unitPrice *
                                                                      exam.quantity) /
                                                                  100
                                                              ).toLocaleString(
                                                                  'pt-BR',
                                                                  {
                                                                      minimumFractionDigits: 2,
                                                                      maximumFractionDigits: 2,
                                                                  }
                                                              )}`}
                                                    </td>
                                                    <td className="text-center">
                                                        {exam.complimentary ? (
                                                            <span className="badge badge-xs badge-info">
                                                                Cortesia
                                                            </span>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </td>
                                                    <td className="text-xs">
                                                        {/* Na venda de pacote quem
                                                            executa não está na linha do
                                                            atendimento: mora no cadastro
                                                            do pacote e vira linha de
                                                            repasse na cobrança. Casamos
                                                            pelo nome do componente, que
                                                            é o que foi congelado ali. */}
                                                        {!(exam as any)
                                                            .recipientKind &&
                                                        payoutByComponent.get(
                                                            exam.catalog
                                                                ?.name ?? ''
                                                        ) ? (
                                                            <>
                                                                {payoutByComponent
                                                                    .get(
                                                                        exam
                                                                            .catalog
                                                                            ?.name ??
                                                                            ''
                                                                    )!
                                                                    .map(
                                                                        (
                                                                            r: any,
                                                                            i: number
                                                                        ) => (
                                                                            <div
                                                                                key={
                                                                                    i
                                                                                }
                                                                                className="mb-1 last:mb-0"
                                                                            >
                                                                                <span className="font-medium">
                                                                                    {r.name ||
                                                                                        `#${r.entityId}`}
                                                                                </span>
                                                                                <p className="text-base-content/50">
                                                                                    {RECIPIENT_KIND_LABELS[
                                                                                        r
                                                                                            .kind
                                                                                    ] ||
                                                                                        r.kind}{' '}
                                                                                    ·{' '}
                                                                                    {formatCurrency(
                                                                                        r.amount
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                        )
                                                                    )}
                                                            </>
                                                        ) : (exam as any)
                                                              .recipientKind ? (
                                                            <>
                                                                <span className="font-medium">
                                                                    {(
                                                                        exam as any
                                                                    )
                                                                        .recipientName ||
                                                                        `#${(exam as any).recipientId}`}
                                                                </span>
                                                                <p className="text-base-content/50">
                                                                    {RECIPIENT_KIND_LABELS[
                                                                        (
                                                                            exam as any
                                                                        )
                                                                            .recipientKind as keyof typeof RECIPIENT_KIND_LABELS
                                                                    ] ||
                                                                        (
                                                                            exam as any
                                                                        )
                                                                            .recipientKind}
                                                                    {(
                                                                        exam as any
                                                                    )
                                                                        .recipientAmount
                                                                        ? ` · ${formatCurrency((exam as any).recipientAmount)}`
                                                                        : ''}
                                                                </p>
                                                            </>
                                                        ) : (
                                                            <span className="text-base-content/40">
                                                                Sem repasse
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-xs whitespace-nowrap">
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost btn-xs gap-1"
                                                            title="Remarcar só este componente"
                                                            onClick={() =>
                                                                setReschedulingComponent(
                                                                    {
                                                                        id: exam.id,
                                                                        name:
                                                                            exam
                                                                                .catalog
                                                                                ?.name ||
                                                                            'Componente',
                                                                        scheduledAt:
                                                                            (
                                                                                exam as any
                                                                            )
                                                                                .scheduledAt,
                                                                        arrivalOrder:
                                                                            (
                                                                                exam as any
                                                                            )
                                                                                .arrivalOrder,
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            <MdEventRepeat className="text-sm" />
                                                            {(exam as any)
                                                                .scheduledAt ? (
                                                                formatAppointmentDateTime(
                                                                    (
                                                                        exam as any
                                                                    )
                                                                        .scheduledAt,
                                                                    (
                                                                        exam as any
                                                                    )
                                                                        .arrivalOrder
                                                                )
                                                            ) : (
                                                                <span className="text-base-content/40">
                                                                    A marcar
                                                                </span>
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="font-semibold">
                                                <td
                                                    colSpan={4}
                                                    className="text-right"
                                                >
                                                    Total:
                                                </td>
                                                <td className="text-right text-secondary">
                                                    R${' '}
                                                    {(
                                                        items.reduce(
                                                            (sum, e) =>
                                                                sum +
                                                                (e.complimentary
                                                                    ? 0
                                                                    : e.unitPrice *
                                                                      e.quantity),
                                                            0
                                                        ) / 100
                                                    ).toLocaleString('pt-BR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                                {items.some(e => e.notes) && (
                                    <div className="mt-4 space-y-2">
                                        {items
                                            .filter(e => e.notes)
                                            .map(e => (
                                                <div
                                                    key={e.id}
                                                    className="text-xs p-2 bg-base-200/50 rounded-lg"
                                                >
                                                    <span className="font-medium">
                                                        {e.catalog?.name}:{' '}
                                                    </span>
                                                    <span className="text-base-content/70">
                                                        {e.notes}
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                })()}

            {/* Materiais utilizados no procedimento (OrderMedicalSupply). */}
            {(order as any).medicalSupplies &&
                (order as any).medicalSupplies.length > 0 && (
                    <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-info/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                            <div className="flex items-center gap-2">
                                <MdVaccines className="text-xl text-info" />
                                <h3 className="text-lg font-bold text-base-content">
                                    Materiais Utilizados
                                </h3>
                                <span className="badge badge-info badge-sm">
                                    {(order as any).medicalSupplies.length}
                                </span>
                            </div>
                        </div>

                        <div className="p-6 overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr className="text-xs text-base-content/50">
                                        <th>Material</th>
                                        <th className="text-center">Qtd.</th>
                                        <th className="text-right">
                                            Valor de venda
                                        </th>
                                        {/* Custo e destinatário: é o que vira conta a
                                            pagar ao fornecedor ou ao hospital. Ficavam
                                            gravados e invisíveis. */}
                                        <th className="text-right">
                                            Custo (repasse)
                                        </th>
                                        <th>Quem recebe</th>
                                        <th className="text-right">Subtotal</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(order as any).medicalSupplies.map(
                                        (ms: any) => {
                                            // Valor cobrado é SEMPRE o `unitPrice` gravado no
                                            // agendamento/orçamento. Não cair para o custo:
                                            // zerar o material no orçamento é uma decisão do
                                            // operador (cortesia/incluso), e o fallback fazia
                                            // reaparecer o preço do cadastro do material.
                                            const unit = ms.unitPrice ?? 0
                                            return (
                                                <tr key={ms.id}>
                                                    <td className="font-medium">
                                                        {ms.medicalSupply
                                                            ?.name ||
                                                            `Material #${ms.medicalSupplyId}`}
                                                        {ms.notes && (
                                                            <p className="text-xs font-normal text-base-content/60 mt-0.5">
                                                                {ms.notes}
                                                            </p>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {ms.quantity}
                                                        {ms.medicalSupply?.unit
                                                            ? ` ${ms.medicalSupply.unit}`
                                                            : ''}
                                                    </td>
                                                    <td className="text-right">
                                                        R${' '}
                                                        {(unit / 100).toFixed(
                                                            2
                                                        )}
                                                    </td>
                                                    <td className="text-right">
                                                        {ms.costPriceSnapshot ? (
                                                            formatCurrency(
                                                                ms.costPriceSnapshot
                                                            )
                                                        ) : (
                                                            <span
                                                                className="text-warning"
                                                                title="Sem custo, o repasse a quem forneceu sai zero"
                                                            >
                                                                sem custo
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-xs">
                                                        {/* "Sem destinatário" só quando
                                                            realmente não há um. Se o id
                                                            existe e só o NOME não veio,
                                                            mostra o id — dizer que não há
                                                            destinatário seria alarme
                                                            falso sobre dinheiro. */}
                                                        {ms.recipientId ||
                                                        ms.recipientName ? (
                                                            <>
                                                                <span className="font-medium">
                                                                    {ms.recipientName ||
                                                                        `#${ms.recipientId}`}
                                                                </span>
                                                                <p className="text-base-content/50">
                                                                    {RECIPIENT_KIND_LABELS[
                                                                        ms.recipientKind ??
                                                                            'SUPPLIER'
                                                                    ] ?? ''}
                                                                    {ms.recipientFromRegistry
                                                                        ? ' · do cadastro'
                                                                        : ''}
                                                                </p>
                                                            </>
                                                        ) : (
                                                            <span
                                                                className="text-warning"
                                                                title="Sem destinatário, o custo é cobrado e a conta a pagar não nasce"
                                                            >
                                                                sem destinatário
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-right font-medium text-secondary">
                                                        {/* Material de pacote entra
                                                            INCLUSO: não é cobrado à
                                                            parte — o custo é que vira
                                                            repasse. Zerado sem
                                                            explicação parecia material
                                                            sem preço. */}
                                                        {isPackageSale(order) &&
                                                        !unit ? (
                                                            <span className="text-base-content/50 font-normal">
                                                                Incluso
                                                            </span>
                                                        ) : (
                                                            `R$ ${(
                                                                (unit *
                                                                    ms.quantity) /
                                                                100
                                                            ).toLocaleString(
                                                                'pt-BR',
                                                                {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2,
                                                                }
                                                            )}`
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className="font-semibold">
                                        <td colSpan={5} className="text-right">
                                            {isPackageSale(order)
                                                ? 'Custo dos Materiais:'
                                                : 'Total dos Materiais:'}
                                        </td>
                                        <td className="text-right text-secondary">
                                            R${' '}
                                            {(
                                                (
                                                    order as any
                                                ).medicalSupplies.reduce(
                                                    (s: number, ms: any) =>
                                                        s +
                                                        // No pacote o cobrado é zero
                                                        // (vai no preço fechado); o que
                                                        // há para somar é o CUSTO.
                                                        (isPackageSale(order)
                                                            ? (ms.costPriceSnapshot ??
                                                              0)
                                                            : (ms.unitPrice ??
                                                              0)) *
                                                            ms.quantity,
                                                    0
                                                ) / 100
                                            ).toLocaleString('pt-BR', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                )}

            {/* Produtos entregues no pacote.
                Estavam gravados (`OrderProduct`, com custo e fornecedor congelados) e
                não apareciam em lugar nenhum da tela — dado invisível é dívida silenciosa.
                SEM valores: eles já estão dentro do preço do pacote, a linha diz o que o
                paciente recebe. Ver docs/catalog-package-products.md. */}
            {(order as any).products && (order as any).products.length > 0 && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-info/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <div className="flex items-center gap-2">
                            <MdInventory2 className="text-xl text-info" />
                            <h3 className="text-lg font-bold text-base-content">
                                Produtos do Pacote
                            </h3>
                            <span className="badge badge-info badge-sm">
                                {(order as any).products.length}
                            </span>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {(order as any).products.map((p: any) => (
                                <div
                                    key={p.id}
                                    className="flex items-start justify-between gap-3 p-3 bg-base-200/50 rounded-xl"
                                >
                                    <div className="min-w-0">
                                        <span className="font-medium">
                                            {p.name ||
                                                p.product?.name ||
                                                'Produto'}
                                        </span>
                                        {/* O produto do pacote vem INCLUSO: o que
                                                importa é o custo, que vira repasse ao
                                                fornecedor. Sem isso a seção mostrava só
                                                o nome e a quantidade. */}
                                        <p className="text-xs text-base-content/60">
                                            {p.supplier?.name
                                                ? `Fornecedor: ${p.supplier.name}`
                                                : 'Sem fornecedor'}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-sm text-base-content/60">
                                            {p.quantity}x
                                        </p>
                                        <p className="text-sm font-medium">
                                            {p.costPriceSnapshot
                                                ? `Custo: ${formatCurrency(p.costPriceSnapshot * (p.quantity || 1))}`
                                                : 'Sem custo'}
                                        </p>
                                        {p.unitPrice > 0 && (
                                            <p className="text-xs text-base-content/60">
                                                Cobrado:{' '}
                                                {formatCurrency(
                                                    p.unitPrice *
                                                        (p.quantity || 1)
                                                )}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {order.team && order.team.length > 0 && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <div className="flex items-center gap-2">
                            <MdGroup className="text-xl text-secondary" />
                            <h3 className="text-lg font-bold text-base-content">
                                Equipe
                            </h3>
                            <span className="badge badge-secondary badge-sm">
                                {order.team.length}
                            </span>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {order.team.map(member => (
                                <div
                                    key={member.id}
                                    className="flex items-center gap-3 p-3 bg-base-200/50 rounded-xl"
                                >
                                    <div className="p-2 bg-secondary/10 rounded-lg">
                                        <MdPerson className="text-lg text-secondary" />
                                    </div>
                                    <div>
                                        <p className="font-medium">
                                            {member.professional?.name ||
                                                'Profissional'}
                                        </p>
                                        {member.role && (
                                            <p className="text-sm text-base-content/60">
                                                {member.role}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {(order.complications || order.outcome) && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <h3 className="text-lg font-bold text-base-content">
                            Resultado
                        </h3>
                    </div>

                    <div className="p-6 space-y-4">
                        {order.complications && (
                            <div>
                                <h4 className="font-semibold text-base-content mb-2">
                                    Complicações
                                </h4>
                                <div className="p-4 bg-error/10 rounded-xl border border-error/20">
                                    <p className="text-base-content whitespace-pre-wrap">
                                        {order.complications}
                                    </p>
                                </div>
                            </div>
                        )}

                        {order.outcome && (
                            <div>
                                <h4 className="font-semibold text-base-content mb-2">
                                    Desfecho
                                </h4>
                                <div className="p-4 bg-success/10 rounded-xl border border-success/20">
                                    <p className="text-base-content whitespace-pre-wrap">
                                        {order.outcome}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {order.notes && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <h3 className="text-lg font-bold text-base-content">
                            Observações
                        </h3>
                    </div>

                    <div className="p-6">
                        <p className="text-base-content whitespace-pre-wrap">
                            {order.notes}
                        </p>
                    </div>
                </div>
            )}

            {order.attachments && order.attachments.length > 0 && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <div className="flex items-center gap-2">
                            <MdAttachFile className="text-xl text-secondary" />
                            <h3 className="text-lg font-bold text-base-content">
                                Anexos
                            </h3>
                            <span className="badge badge-secondary badge-sm">
                                {order.attachments.length}
                            </span>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="flex flex-col gap-3">
                            {order.attachments.map((attachment, index) => (
                                <div
                                    key={index}
                                    className="bg-base-200/50 rounded-xl border border-base-300/50 p-4"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <MdAttachFile className="text-lg text-secondary" />
                                                <a
                                                    href={attachment.filePath}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-semibold text-secondary hover:underline"
                                                >
                                                    {attachment.fileName}
                                                </a>
                                            </div>
                                            <div className="space-y-1 text-sm">
                                                <p>
                                                    <span className="font-medium">
                                                        Usuário:
                                                    </span>{' '}
                                                    {attachment.userName}
                                                </p>
                                                <p>
                                                    <span className="font-medium">
                                                        Descrição:
                                                    </span>{' '}
                                                    {attachment.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Funil de vendas por ÚLTIMO: é contexto comercial, não o atendimento.
                No meio da página ele separava os valores do procedimento das listas de
                itens, materiais e componentes, que são lidos em sequência. */}
            {order.deal && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4 flex items-center gap-2">
                            <MdTrendingUp className="text-secondary" />
                            Funil de Vendas
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-sm text-base-content/60">
                                    Pipeline
                                </span>
                                <p className="font-medium">
                                    {order.deal.pipeline.name}
                                </p>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">
                                    Etapa
                                </span>
                                <div className="flex items-center gap-2">
                                    <span
                                        className="w-3 h-3 rounded-full inline-block"
                                        style={{
                                            backgroundColor:
                                                order.deal.stage.color ||
                                                '#6b7280',
                                        }}
                                    />
                                    <span className="font-medium">
                                        {order.deal.stage.name}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">
                                    Oportunidade
                                </span>
                                <p className="font-medium">
                                    {order.deal.title}
                                </p>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">
                                    Status
                                </span>
                                <div>
                                    <span
                                        className={`badge badge-sm ${order.deal.status === 'WON' ? 'badge-success' : order.deal.status === 'LOST' ? 'badge-error' : order.deal.status === 'ABANDONED' ? 'badge-warning' : 'badge-info'}`}
                                    >
                                        {order.deal.status === 'OPEN'
                                            ? 'Aberta'
                                            : order.deal.status === 'WON'
                                              ? 'Ganha'
                                              : order.deal.status === 'LOST'
                                                ? 'Perdida'
                                                : 'Abandonada'}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">
                                    Lead
                                </span>
                                <p className="font-medium">
                                    {order.deal.lead.name}
                                </p>
                                {order.deal.lead.phoneNumber && (
                                    <p className="text-sm text-base-content/60">
                                        {order.deal.lead.phoneNumber}
                                    </p>
                                )}
                            </div>
                            {order.deal.totalValue != null &&
                                order.deal.totalValue > 0 && (
                                    <div>
                                        <span className="text-sm text-base-content/60">
                                            Valor da Oportunidade
                                        </span>
                                        <p className="font-medium text-secondary">
                                            {(
                                                order.deal.totalValue / 100
                                            ).toLocaleString('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL',
                                            })}
                                        </p>
                                    </div>
                                )}
                        </div>
                        <div className="mt-4">
                            <a
                                href={`/sales/opportunities/${order.deal.id}`}
                                className="btn btn-sm btn-outline btn-secondary"
                            >
                                Ver Oportunidade
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {order && (
                <SignDocumentModal
                    isOpen={showSignModal}
                    onClose={() => setShowSignModal(false)}
                    documentType="PROCEDURE"
                    documentId={order.id}
                    documentTitle={
                        isGoodsSale
                            ? `Venda - ${order.title || ''}`
                            : `${OrderLabel} - ${order.catalog?.name || ''}`
                    }
                    onSigned={() => setShowSignModal(false)}
                />
            )}

            <RescheduleComponentModal
                isOpen={!!reschedulingComponent}
                onClose={() => setReschedulingComponent(null)}
                loading={reschedulingComponentLoading}
                componentName={reschedulingComponent?.name ?? ''}
                currentScheduledAt={reschedulingComponent?.scheduledAt}
                currentArrivalOrder={reschedulingComponent?.arrivalOrder}
                onConfirm={async input => {
                    if (!reschedulingComponent || !order) return
                    setReschedulingComponentLoading(true)
                    try {
                        const res = await rescheduleOrderComponent(
                            order.id,
                            reschedulingComponent.id,
                            input
                        )
                        if (!res.success) {
                            // Conflito de horário volta como erro do backend: a mensagem
                            // dele é mais útil que um texto genérico.
                            toast.error(
                                res.error || 'Erro ao remarcar o componente'
                            )
                            return
                        }
                        toast.success('Componente remarcado')
                        setReschedulingComponent(null)
                        await loadOrder()
                    } finally {
                        setReschedulingComponentLoading(false)
                    }
                }}
            />

            <CancelAppointmentModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={confirmCancel}
                loading={cancelling}
                entityLabel={order.packageCatalogId ? 'pacote' : orderLabel}
                billing={order.billing ?? null}
            />

            <ConfirmModal
                isOpen={showRenewGuideModal}
                onClose={() => setShowRenewGuideModal(false)}
                onConfirm={handleRenewGuide}
                title="Renovar guia"
                message={`Renovar a validade da guia para ${format(
                    computeRenewedGuideValidUntil(guideValidityDays),
                    "dd 'de' MMMM 'de' yyyy",
                    { locale: ptBR }
                )}?`}
                confirmButtonText="Renovar"
                cancelButtonText="Cancelar"
                loading={renewingGuide}
            />

            {showStatusModal && (
                <ChangeStatusModal
                    entityLabel={entityLabel}
                    currentStatus={order.status}
                    options={statusOptions}
                    saving={savingStatus}
                    onClose={() => setShowStatusModal(false)}
                    onConfirm={handleChangeStatus}
                />
            )}

            <ScheduleAppointmentModal
                isOpen={showScheduleModal}
                onClose={() => setShowScheduleModal(false)}
                serviceType="PROCEDURE"
                recordId={order.id}
                currentScheduledAt={order.scheduledAt}
                currentArrivalOrder={(order as any).arrivalOrder}
                onScheduled={loadOrder}
            />

            <TransferSellerModal
                isOpen={showTransferModal}
                onClose={() => setShowTransferModal(false)}
                onConfirm={confirmTransferSeller}
                loading={transferring}
                currentSellerName={order.createdBy?.name}
                companyId={order.companyId}
            />

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title={isGoodsSale ? 'Excluir venda' : `Excluir ${orderLabel}`}
                message={
                    isGoodsSale
                        ? 'Tem certeza que deseja excluir esta venda?'
                        : `Tem certeza que deseja excluir este ${orderLabel}?`
                }
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />

            <ApproveBudgetModal
                isOpen={showApproveModal}
                onClose={() => setShowApproveModal(false)}
                onConfirm={handleApproveBudget}
                loading={approvingBudget}
                // Base da simulação: o valor de VENDA, sem taxa. `finalPrice` é o
                // que a cobrança persegue; `totalPrice` cobre o registro antigo.
                amount={(order as any)?.finalPrice || order?.totalPrice || 0}
                healthPlanName={(order as any)?.healthPlan?.name ?? null}
                partnerId={order?.partnerId ?? null}
                professionalId={order?.professionalId ?? null}
                hospitalId={order?.hospitalId ?? null}
                entityLabel="procedimento"
            />

            <ChangeServiceAddressModal
                isOpen={showAddressModal}
                onClose={() => setShowAddressModal(false)}
                onConfirm={handleChangeServiceAddress}
                loading={savingAddress}
                current={order}
                partnerId={order?.partnerId ?? null}
                professionalId={order?.professionalId ?? null}
                hospitalId={order?.hospitalId ?? null}
                entityLabel="procedimento"
            />

            <GenerateBillingModal
                isOpen={showGenerateBillingModal}
                onClose={() => setShowGenerateBillingModal(false)}
                onConfirm={handleGenerateBilling}
                chargeable={
                    billingState.shouldBecomeAnchor
                        ? orderFinalPrice
                        : billingState.remainingToBill
                }
                allowPartial
                entityLabel={entityLabel}
                loading={generatingBilling}
                appointment={
                    order ? { type: 'PROCEDURE', id: order.id } : undefined
                }
                onOrphanLinked={loadOrder}
            />

            <SettleOrderModal
                isOpen={showSettleModal}
                onClose={() => setShowSettleModal(false)}
                onConfirm={handleRequestSettlement}
                settlement={settlement}
                loadingPreview={loadingSettlement}
                loading={requestingSettlement}
            />

            <ConfirmModal
                isOpen={showEnableFlexModal}
                onClose={() => setShowEnableFlexModal(false)}
                onConfirm={handleEnableFlex}
                loading={enablingFlex}
                title="Ativar pagamento Flex"
                message="O procedimento passa a aceitar pagamentos avulsos do cliente pelo portal, que escolhe valor e vencimento até fechar o total. As cobranças já existentes são mantidas e atreladas. Deseja continuar?"
                confirmButtonText="Ativar pagamento flex"
            />

            {/* Mesmo modal (o local de atendimento é confirmado igual), mas a
                aprovação sai em modo flex — sem cobrança. */}
            <ApproveBudgetModal
                isOpen={showApproveFlexModal}
                onClose={() => setShowApproveFlexModal(false)}
                onConfirm={address =>
                    handleApproveBudget(address, undefined, true)
                }
                hidePayment
                loading={approvingBudget}
                partnerId={order?.partnerId ?? null}
                professionalId={order?.professionalId ?? null}
                hospitalId={order?.hospitalId ?? null}
                entityLabel="procedimento"
            />
        </div>
    )
}
